import { Logo } from "@paribus/shared-ui";
import { Link, useLocation } from "react-router-dom";

import { AUTH_PATHS } from "../../constants";
import { FeatureFlag } from "../../constants/featureFlags";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { buildTeamMailtoHref, buildWaitlistHref } from "../../utils/waitlistContact";
import { PricingSection } from "../Plans/Plans";
import { DemoSection } from "./DemoSection";
import { TalkToUs } from "./TalkToUs";

export const LandingPage = () => {
  const location = useLocation();
  const signupEnabled = useFeatureFlag(FeatureFlag.SignupEnabled);
  const primaryCtaHref = !signupEnabled
    ? buildWaitlistHref(location.search)
    : `${AUTH_PATHS.SIGNUP}${location.search}`;
  const primaryCtaLabel = !signupEnabled ? "Join waitlist" : "Get Started";
  const waitlistHref = buildWaitlistHref(location.search);
  const enterpriseContactHref = buildTeamMailtoHref();

  return (
    <div className="min-h-screen bg-primary-subtle">
      <nav className="sticky top-0 z-sticky border-b border-border-subtle/50 bg-card/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <Logo size="md" />
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to={AUTH_PATHS.SIGNIN}
                className="whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-surface-subtle hover:text-foreground"
              >
                Log in
              </Link>
              <Link
                to={primaryCtaHref}
                className="whitespace-nowrap rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-hover hover:shadow-md active:scale-press"
              >
                {primaryCtaLabel}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-primary-subtle opacity-70"></div>
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold tracking-tight text-foreground sm:text-6xl md:text-7xl">
              <span className="block bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text pb-2 text-transparent">
                Discover the elegant power of{" "}
              </span>
              <span className="mt-2 block text-primary">Paribus AI</span>
            </h1>
            <p className="mx-auto mt-6 max-w-md text-lg text-muted-foreground sm:text-xl md:mt-8 md:max-w-3xl md:text-2xl">
              Fast & High-quality AI + Human QA injury record review for medical examiners and
              med-legal management companies.
            </p>
            <div className="mx-auto mt-8 max-w-md sm:flex sm:justify-center md:mt-12">
              <div className="rounded-xl shadow-sm">
                <Link
                  to={primaryCtaHref}
                  className="flex w-full transform items-center justify-center whitespace-nowrap rounded-xl border border-transparent bg-primary px-8 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-primary-hover hover:shadow-md md:px-12 md:py-5 md:text-lg"
                >
                  {primaryCtaLabel}
                </Link>
              </div>
              <div className="mt-3 rounded-xl shadow-sm sm:ml-4 sm:mt-0">
                <Link
                  to={waitlistHref}
                  className="flex w-full transform items-center justify-center whitespace-nowrap rounded-xl border border-border-subtle bg-card px-8 py-4 text-base font-semibold text-muted-foreground transition-all hover:-translate-y-0.5 hover:border-primary-border hover:text-primary hover:shadow-md md:px-12 md:py-5 md:text-lg"
                  aria-label="Schedule a Paribus demo from the introduction"
                >
                  Schedule a demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DemoSection />

      {/* Security Section */}
      <div className="bg-primary-subtle py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-primary-subtle-hover">
              <svg
                className="h-10 w-10 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="mb-6 text-4xl font-bold text-foreground">
              Rest easy with rock-solid security
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-xl text-muted-foreground">
              Your data never leaves our HIPAA-compliant platform. We maintain the highest security
              standards to protect your sensitive client information.
            </p>
          </div>
        </div>
      </div>

      <PricingSection enterpriseContactHref={enterpriseContactHref} />

      <TalkToUs />
    </div>
  );
};
